/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import {genericErrorHasOccurred} from '../../../actions/generic-error/action-creators';
import {optileInitSucceeded} from '../../../actions/optile/action-creators';
import propLoaders from '../../../prop-loaders';
import assetUrl from '../../../utils/assetUrl';

import {PaymentFormTemplate} from '../../organisms';
import Spinner from '../../atoms/Spinner';
import {
    getTranslations,
    getGenericError,
    getLanguageCode,
    getAvailableLanguageCodes,
    getTracking, getCustomer,
    getPlatformData,
    getCalculation
} from '../../../utils/selectors';
import localiseUrl from '../../../utils/localiseUrl';
import {isOptileLoaded} from '../../../selectors/optile';
import {pushAnalyticsEvent} from '../../organisms/Analytics';
import {setPageLoadEvent} from '../../organisms/Analytics/pageLoadEvent';
import {setCalculation} from '../../../actions/common/action-creators';

import(/* webpackChunkName: "op-payment-widget" */ '../../../../assets/css/op-payment-widget.css');

// temp: additional logs to track optile issue on greenfield ios
if (typeof jQuery !== 'undefined') {
    jQuery(document).ajaxError((event, xhr, options = {}, error) => {
        window.sendLog({
            message: `Error sending ${options.type} request to ${options.url}: ${error}`,
            url: options.url,
            type: options.type
        });
    });
}

const Optile = ({
    translations,
    dataInit,
    cancellationUrl,
    loaded,
    tracking,
    customer,
    platformData,
    calculation
}) => {
    const {iframeUrl} = dataInit;

    const isNewCard = useRef(false);
    const isSubmitForm = useRef(false);
    const isInit = useRef(false);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setPageLoadEvent({
            tracking,
            customer,
            platformData,
            isSavedCardsPage: false,
            provider: 'optile',
            calculation
        });
    }, []);
    /* eslint-enable react-hooks/exhaustive-deps */

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (isSubmitForm.current) {
                pushAnalyticsEvent({
                    interactionName: 'form submit',
                    formName: isNewCard.current ? 'optile-payment-form' : 'optile-payment-form-saved-card',
                    formResult: 'unknown'
                });
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);


    useEffect(() => {
        if (loaded) {
            pushAnalyticsEvent({
                interactionName: 'form loaded',
                formName: 'optile-payment-form'
            });
        }
    }, [loaded]);

    useEffect(() => {
        const isMobile = window.screen.width <= 767;

        // Run library
        if (window.checkoutList && iframeUrl) {
            window.sendLog({
                message: 'optile checkoutList', iframeUrl
            });
            window.checkoutList('optileIframeId', {
                payButton: 'submitButton',
                listUrl: iframeUrl,
                smartSwitch: true,
                iFrameScaleFunction() {
                    return isMobile ? 1.8 : 3.2;
                },
                cardView: isMobile,
                widgetCssUrl: window.location.origin + assetUrl('op-payment-widget.css')
            });
        }

        return () => { };
    }, [iframeUrl]);


    const handleSubmit = () => {
        isSubmitForm.current = true;
        const cardContainer = document.getElementById('CREDIT_CARD');

        isNewCard.current = !!cardContainer?.classList.contains('selected');
    };

    const handleFormClick = e => {
        if (isInit.current) {
            if (e.target.tagName === 'SPAN' && e.target.classList.contains('paymentform-nav-link-text')) {
                isSubmitForm.current = false;
            } else if (e.target.type === 'checkbox' && e.target.id.includes('Registration')) {
                pushAnalyticsEvent({
                    interactionName: 'card payment interaction',
                    cardPaymentAction: e.target.checked ? 'save this card toggled on' : 'save this card toggled off'
                });
            } else if (e.target.type === 'button' && e.target.id === 'submit-delete') {
                pushAnalyticsEvent({
                    interactionName: 'card payment interaction',
                    cardPaymentAction: 'remove saved card'
                });
            } else if (e.target.type === 'radio' && e.target.id.includes('paymentTyperegistered')) {
                pushAnalyticsEvent({
                    interactionName: 'card payment interaction',
                    cardPaymentAction: 'click on saved card'
                });
            } else if (e.target.type === 'radio' && e.target.id === 'paymentTypeCREDIT_CARD') {
                pushAnalyticsEvent({
                    interactionName: 'card payment interaction',
                    cardPaymentAction: 'add new card'
                });
            }
        }

        isInit.current = true;
    };

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="optile-container" onClick={handleFormClick}>
            {!loaded && <Spinner />}

            <PaymentFormTemplate
                translations={translations}
                cancellationUrl={cancellationUrl}
                title={translations.formHeader.payMethod}
            >
                <div
                    id="optileIframeId"
                    className="optile-form"
                    aria-hidden="true"
                />
                <button
                    id="submitButton"
                    className="submit-button button-mega-cta"
                    type="button"
                    onClick={handleSubmit}
                >
                    <span className="submit-button-content-container">
                        <span className="submit-button-content">
                            {translations.optile.form.submitButton}
                        </span>
                        <span className="submit-button-icon icon-chevron-right" />
                    </span>

                </button>
            </PaymentFormTemplate>
        </div>
    );
};

Optile.getServerSideProps = async ({
    requestId,
    sessionId,
    params: {
        language: languageCode,
        sessionId: paymentSessionId
    },
    session
}, store) => {
    try {
        const data = await propLoaders.getOptileInit({
            requestId,
            sessionId,
            query: {
                languageCode,
                paymentSessionId
            }
        });

        store.dispatch(optileInitSucceeded(data));
        store.dispatch(setCalculation(session.calculation));
    } catch (e) {
        store.dispatch(genericErrorHasOccurred(true));
    }
};

Optile.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            sessionId: PropTypes.string.isRequired
        }).isRequired
    }).isRequired,
    translations: PropTypes.shape().isRequired,
    cancellationUrl: PropTypes.string,
    dataInit: PropTypes.shape({}).isRequired,
    calculation: PropTypes.shape({})
};

Optile.defaultProps = {
    cancellationUrl: '',
    calculation: null
};

const mapStateToProps = state => {
    const languageCode = getLanguageCode(state);
    const availableLanguageCodes = getAvailableLanguageCodes(state);
    const {cancelUrl} = state.optileData.dataInit;

    return {
        languageCode,
        translations: getTranslations(state),
        loaded: isOptileLoaded(state),
        cancellationUrl: localiseUrl(cancelUrl, languageCode, availableLanguageCodes),
        dataInit: state.optileData.dataInit,
        genericError: getGenericError(state),
        tracking: getTracking(state),
        customer: getCustomer(state),
        platformData: getPlatformData(state),
        calculation: getCalculation(state)
    };
};

export const ConnectedOptile = connect(mapStateToProps)(Optile);

export default withRouter(ConnectedOptile);
